<template>
  <TooltipProvider>
    <UNavigationMenu
      v-bind="{ ...props, ...$attrs }"
      :items="nativeItems"
      :trailing-icon="nativeIcon(props.trailingIcon)"
      :external-icon="props.externalIcon === undefined || props.externalIcon === true"
      class="p-navigation-menu"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template v-for="name in forwardedSlots" #[name]="slotProps">
        <slot :name="name" v-bind="slotProps" />
      </template>
      <template #item-label="slotProps">
        <slot name="item-label" v-bind="slotProps">
          <span class="block"
            >{{ label(slotProps.item) }}
            <component
              v-if="
                slotProps.item.target === '_blank' &&
                props.externalIcon &&
                props.externalIcon !== true
              "
              :is="nativeIcon(props.externalIcon)"
              data-slot="linkLabelExternalIcon"
              class="inline-block size-3"
            />
          </span>
          <span
            v-if="slotProps.item.description && !props.collapsed"
            class="block text-xs/4 font-normal"
            >{{ slotProps.item.description }}</span
          >
        </slot>
      </template>
    </UNavigationMenu>
  </TooltipProvider>
</template>

<script lang="ts">
export type {
  PNavigationMenuProps,
  PNavigationMenuItem,
  PNavigationMenuChildItem,
  PNavigationMenuSlots,
  PNavigationMenuValue
} from "./navigation-menu.types"
</script>

<script setup lang="ts">
import { computed, defineComponent, h, normalizeClass } from "vue"
import { TooltipProvider } from "reka-ui"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import UNavigationMenu, { type NavigationMenuItem } from "@nuxt/ui/components/NavigationMenu.vue"
import type {
  PNavigationMenuProps,
  PNavigationMenuItem,
  PNavigationMenuIcon,
  PNavigationMenuSlots,
  PNavigationMenuValue
} from "./navigation-menu.types"

const props = withDefaults(defineProps<PNavigationMenuProps>(), {
  orientation: "horizontal",
  type: "multiple",
  color: "primary",
  variant: "pill",
  valueKey: "value",
  labelKey: "label",
  externalIcon: true,
  unmountOnHide: true,
  collapsible: true
})
const emit = defineEmits<{ "update:modelValue": [value: PNavigationMenuValue | undefined] }>()
const slots = defineSlots<PNavigationMenuSlots>()
defineOptions({ inheritAttrs: false })

// Keep FontAwesome's existing icon API while Nuxt handles the menu itself.
function nativeIcon(icon: PNavigationMenuIcon | undefined) {
  if (!icon || (typeof icon === "string" && (icon.startsWith("i-") || icon.includes(":"))))
    return icon
  if (
    typeof icon === "function" ||
    (typeof icon === "object" && !Array.isArray(icon) && !("icon" in icon))
  )
    return icon
  return defineComponent({
    setup: () => () => h(FontAwesomeIcon, { icon: icon as IconProp, "aria-hidden": true })
  })
}
function nativeItem(item: PNavigationMenuItem): NavigationMenuItem {
  return {
    ...item,
    icon: nativeIcon(item.loading ? faSpinner : item.icon),
    ui: {
      ...item.ui,
      linkLeadingIcon: normalizeClass([
        item.ui?.linkLeadingIcon,
        item.loading && "animate-spin motion-reduce:animate-none"
      ])
    },
    trailingIcon: nativeIcon(item.trailingIcon),
    avatar: item.avatar ? { ...item.avatar, icon: nativeIcon(item.avatar.icon) } : undefined,
    disabled: props.disabled || item.disabled || item.loading,
    "aria-busy": item.loading || undefined,
    "aria-label": item.ariaLabel ?? (props.collapsed ? label(item) : undefined),
    children: item.children?.map(nativeItem)
  }
}
const nativeItems = computed(
  () =>
    props.items?.map((item) => (Array.isArray(item) ? item.map(nativeItem) : nativeItem(item))) as
      NavigationMenuItem[] | NavigationMenuItem[][] | undefined
)
const forwardedSlots = computed(() =>
  (Object.keys(slots) as (keyof PNavigationMenuSlots)[]).filter((name) => name !== "item-label")
)
function label(item: Record<string, unknown>) {
  return props.labelKey
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === "object" ? (value as Record<string, unknown>)[key] : undefined,
      item
    )
}
</script>
